import React, { useCallback, useEffect } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import millify from "millify";
import ArrowTopRightIcon from "../../../assets/svgs/common/arrow-top-right.svg";
import { InstagramIcon } from "../../../assets/svgs/common/instagram.svg";
import GrowthIcon from "../../../assets/svgs/common/growth.svg";
import type { IGenreMinimal } from "../../../types/models/genre.models.types";
import { Badge } from "../../components/core/badge.core";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../../components/ui/hover-card";
import { Separator } from "../../../components/ui/separator";
import { useToast } from "../../../hooks/use-toast";
import { useSearchProposalSocialsHttp } from "../../hooks/http/proposal-socials/search-proposal-socials.hook";
import { debounce, handleHttpError } from "../../../utils/utils";
import type { IProposalSocial } from "../../hooks/http/proposal-socials/get-proposal-socials.hook";
import Input from "../../components/core/input.core";
import { XIcon } from "lucide-react";
import Spinner from "../../../components/spinner.components";
import { CustomTable } from "../../components/core/custom-table.core";
import { Link, useNavigate, useParams } from "react-router-dom";
import CommentIcon from "../../assets/svgs/comment.svg";
import SearchIcon from "../../assets/svgs/search.svg";
import { AddSocialToProposal } from "./add-social-to-proposal.partial";
import { RemoveSocialFromProposal } from "./remove-social-from-proposal.partial";
import useProposalSocialSearchStore from "../../store/proposal-socials-search.store";
import { useUserStore } from "../../../store/user.store";
import useProposalSocialStore from "../../store/proposal-social.store";
import { ThreeDWrapper } from "../../components/core/3d-wrapper.core";

export default function ProposalSocialsTable({
  data,
  fetchingProposalSocials,
}: {
  data: IProposalSocial[];
  fetchingProposalSocials: boolean;
}) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const navigate = useNavigate();
  const { proposalId } = useParams();
  const { token, user } = useUserStore();
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnPinning, setColumnPinning] = React.useState<
    import("@tanstack/react-table").ColumnPinningState
  >({
    right: ["actions"],
  });

  const [search, setSearch] = React.useState("");
  const {
    setData: setSearchData,
    data: searchData,
    markCommentsRead: markCommentsReadSearch,
  } = useProposalSocialSearchStore();
  const { markCommentsRead } = useProposalSocialStore();
  const [typing, setTyping] = React.useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (token) {
      navigate(`/brands/proposals/${proposalId}`);
    }
  }, [token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const columns = React.useMemo<ColumnDef<IProposalSocial>[]>(
    () => [
      // {
      //   accessorKey: "id",

      //   header: ({ table }) => {
      //     return (
      //       <div className="flex items-center">
      //         <Checkbox
      //           checkboxType="thunder"
      //           className="rounded-full w-5 h-5"
      //           checked={table.getIsAllPageRowsSelected()}
      //           onClick={table.getToggleAllPageRowsSelectedHandler()}
      //         />
      //       </div>
      //     );
      //   },
      //   cell: ({ row }) => {
      //     return (
      //       <div className="flex items-center">
      //         <Checkbox
      //           checkboxType="thunder"
      //           className="rounded-full w-5 h-5"
      //           checked={row.getIsSelected()}
      //           onClick={() => {
      //             row.toggleSelected();
      //           }}
      //         />
      //       </div>
      //     );
      //   },
      // },
      {
        accessorKey: "social.handle",
        header: "User name",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <div className="md:block hidden border-2 rounded-full w-8 h-8" />
              <a
                href={`https://instagram.com/${social.handle}/`}
                target="_blank"
                aria-label="Instagram profile link"
                rel="noreferrer"
                className="flex items-center"
              >
                <p className="w-20 md:w-fit md:max-w-32 text-ellipsis text-nowrap text-xs md:text-base underline overflow-hidden">
                  {social.handle}
                </p>
                <ArrowTopRightIcon className="w-8 h-8" />
              </a>
            </div>
          );
        },
      },

      {
        accessorKey: "proposalSocial.fixedPayoutAmount",
        header: "Cost",

        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <div className="flex items-center gap-0 w-24 text-base">
              <span className="font-medium">
                ₹ {proposalSocial?.fixedPayoutAmount?.amount || 0}
              </span>
            </div>
          ) : (
            <div className="w-24">-</div>
          );
        },
      },

      {
        accessorKey: "social.followersCount",
        header: "Followers & engagement",
        cell: ({ row }) => {
          const { social } = row.original;

          return (
            <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
              <InstagramIcon className="w-5 h-5" />
              <div className="flex justify-start items-center gap-2 w-10 text-xs md:text-base">
                {millify(social.followersCount ?? 0)}
              </div>
              <div className="border-black border-l h-5" />
              <div className="flex justify-start items-center gap-2 w-fit text-xs md:text-base">
                <GrowthIcon />
                {social.engagementRate?.toFixed()}%
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "social.genre",
        header: "Genre",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex flex-wrap items-center gap-2 w-64 text-white">
              {(social.genre?.filter?.length ?? 0) > 0 && (
                <ThreeDWrapper  hover={false} className="flex items-start gap-2 shadow-none p-0 border-none h-fit font-semibold text-secondary">
                  <p className="flex flex-wrap items-center gap-2 text-white">
                    {(() => {
                      const genres: IGenreMinimal[] = Array.isArray(
                        social.genre
                      )
                        ? (social.genre as IGenreMinimal[])
                        : [];
                      const itemsToShow = genres.slice(0, 1);
                      const extraCount = genres.length - itemsToShow.length;
                      return (
                        <>
                          {itemsToShow.map((genre) => (
                            <Badge
                              key={genre.id}
                              variant="outline"
                              className="flex gap-2 border border-black capitalize"
                            >
                              <div>
                                {genre.emoji}
                                {genre.title}
                              </div>
                            </Badge>
                          ))}
                          {extraCount > 0 && (
                            <div className="flex items-center gap-2">
                              <HoverCard>
                                <HoverCardTrigger >
                                  <Badge className="flex justify-center items-center px-2 rounded-full text-black text-xs cursor-pointer">
                                    +{extraCount} more
                                  </Badge>
                                </HoverCardTrigger>
                                <HoverCardContent
                                  align="center"
                                  className="bg-transparent backdrop-blur-lg px-0 py-4 border border-black w-full max-w-96 font-cabinetgrotesk"
                                >
                                  <div className="flex flex-col items-start gap-4 w-full text-black">
                                    <div className="flex items-start px-4 w-full">
                                      Genre
                                    </div>
                                    <Separator className="bg-black w-full" />
                                    <div className="flex flex-wrap items-center gap-2 px-4">
                                      {genres.slice(1).map((genre, index) => (
                                        <span
                                          key={genre.id}
                                          className="flex items-center gap-2 capitalize"
                                        >
                                          <div className="px-4 py-1 border border-black rounded-full text-xs">
                                            {genre.emoji}
                                            {genre.title}
                                          </div>
                                          {index < genres.length - 3 && (
                                            <span className="text-secondary">
                                              <span className="border-border border-r h-2" />
                                            </span>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </p>
                </ThreeDWrapper>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "social.averageViews",
        header: "Average views",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider">
              {millify(social.averageViews ?? 0)}
            </div>
          );
        },
      },
      // {
      //   accessorKey: "averageLikes",
      //   header: "Average likes",
      //   cell: ({ row }) => {
      //     return (
      //       <div className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider">
      //         {row.getValue("averageLikes")}
      //       </div>
      //     );
      //   },
      // },
      {
        accessorKey: "proposalSocial.commentCount",
        header: "Comments",
        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <Link
              to={token ? `comments/${proposalSocial?.id}` : ""}
              onClick={() => {
                proposalSocial.commentCount > 0 &&
                !proposalSocial.commentReadByUsersList.includes(
                  user ? user.id : ""
                )
                  ? search.trim().length > 0
                    ? markCommentsReadSearch(proposalSocial.id, user?.id || "")
                    : markCommentsRead(proposalSocial.id, user?.id || "")
                  : null;
                if (!token) {
                  toast({
                    content: "Please login to view comments",
                    toastType: "error",
                  });
                  return navigate(
                    `/brands/auth/login?proposalId=${proposalId}}`
                  );
                }
              }}
              className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider"
            >
              <div className="relative">
                {proposalSocial.commentCount > 0 &&
                !proposalSocial.commentReadByUsersList.includes(
                  user ? user.id : ""
                ) ? (
                  <div className="top-0 right-0 absolute bg-soloboost rounded-full w-2 h-2" />
                ) : null}

                <CommentIcon />
              </div>
              {proposalSocial?.commentCount}
            </Link>
          ) : (
            "-"
          );
        },
      },
      // {
      //   accessorKey: "creator",
      //   header: "Remarks",
      //   cell: () => {
      //     return (
      //       <Input
      //         type="text"
      //         placeholder="Provide your remarks..."
      //         className="pl-4 w-48 h-12 placeholder:text-secondary"
      //       />
      //     );
      //   },
      // },
      {
        id: "actions", // Change this line
        accessorKey: "social",
        header: "",
        cell: ({ row }) => {
          const { social, proposalSocial } = row.original;

          return (
            <div className="flex justify-end items-center w-36">
              <ActionButton
                proposalSocialId={proposalSocial?.id || ""}
                socialId={social.id}
                searching={search.trim().length > 0}
              />
            </div>
          );
        },
      },
    ],
    [search]
  );

  const table = useReactTable({
    data: search.trim().length > 0 ? searchData : data,
    columns,
    getRowId: (row) =>
      row.proposalSocial ? row.proposalSocial.id : row.social.id,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      columnPinning: {
        right: ["actions"],
      },
    },
    enableColumnPinning: true,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      columnPinning,
    },
    manualPagination: true,
  });

  const { toast } = useToast();

  const {
    execute: searchProposalSocialsHttp,
    fetching: fetchingProposalSocialsSearch,
    cancel: cancelSearchProposalSocialHttp,
  } = useSearchProposalSocialsHttp();

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      if (!proposalId) {
        return;
      }
      const response = await searchProposalSocialsHttp({
        body: null,
        queryParams: {
          query: searchString,
        },
        pathParams: { proposalId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setSearchData(response.data.campaignProposalSocials);
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    if (!token) {
      toast({
        content: "Please login to search",
        toastType: "error",
      });
      navigate(`/brands/auth/login?proposalId=${proposalId}`);
      return;
    }
    cancelSearchProposalSocialHttp();
    debouncedSearch(e.target.value);
    setSearch(e.target.value);
    setTyping(true);
    if (e.target.value.trim().length === 0) {
      setSearchData([]);
      document.getElementById("getProposalSocials")?.click();
    }
  }

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="top-0 z-20 sticky flex items-center gap-4 bg-white px-4 py-4 border-black border-b">
        <div className="relative flex items-center pl-5 border border-soloprimary rounded-xl w-full">
          <SearchIcon />
          <Input
            value={search}
            onChange={(e) => {
              handleSearch(e);
            }}
            className="bg-transparent py-4 border-none font-semibold text-base placeholder:text-secondary focus:ring-0 focus:outline-none"
            placeholder="Search by name, instagram ID, and number"
          />

          {search.trim().length > 0 && (
            <XIcon
              onClick={() => {
                setSearch("");
                setSearchData([]);
                document.getElementById("getProposalSocials")?.click();
              }}
              className="right-5 absolute cursor-pointer"
            />
          )}
        </div>

        {/* <Link to={`${window.location.pathname}/import`}>
            <Button className="flex items-center gap-3 bg-tertiarybuttonbg px-3 py-7 border border-border rounded-xl font-semibold">
              <DocumentSmall />
              Upload CSV
            </Button>
          </Link> */}
      </div>

      {search.trim().length > 0 ? (
        fetchingProposalSocialsSearch || typing ? (
          <div className="flex justify-center items-center pt-20 w-full">
            <Spinner width={20} />
          </div>
        ) : (
          <CustomTable
            table={table}
            columns={columns}
            loading={fetchingProposalSocialsSearch}
          />
        )
      ) : (
        <CustomTable
          table={table}
          columns={columns}
          loading={fetchingProposalSocials}
        />
      )}
    </div>
  );
}

const ActionButton = ({
  proposalSocialId,
  socialId,
  searching,
}: {
  proposalSocialId: string;
  socialId: string;
  searching: boolean;
}) => {
  if (!proposalSocialId) {
    return (
      <div className="relative flex justify-between items-center">
        <AddSocialToProposal id={socialId} />
      </div>
    );
  }

  return (
    <div className="relative flex justify-between items-center">
      <RemoveSocialFromProposal searching={searching} id={proposalSocialId} />
    </div>
  );
};
